import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Container,
  Card,
  Stack,
  Typography,
  Grid,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { styled } from "@mui/material/styles";
import useAPI from "../../../hooks/useAPI";
import { EditValidation  } from "../add/validation";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function UserEditPage() {
  const api = useAPI();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const userData = location.state;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [buttonLoad, setButtonLoad] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(EditValidation ),
  });

  // useEffect(() => {
  //   if (userData) {
  //     // Format date to YYYY-MM-DD for date input
  //     const formattedDate = userData.dateOfBirth
  //       ? new Date(userData.dateOfBirth).toISOString().split("T")[0]
  //       : "";

  //     setValue("username", userData.username);
  //     setValue("email", userData.email);
  //     setValue("phoneno", userData.phoneno);
  //     setValue("gender", userData.gender || "male");
  //     setValue("dateOfBirth", formattedDate);
  //     setValue("userType", userData.userType || "fan");
  //     setValue("country", userData.country || "");
  //     setValue("cityState", userData.cityState || "");
  //     setPreviewImage(userData.avatarUrl);
  //   }
  // }, [userData, setValue]);

  useEffect(() => {
    if (userData) {
        // Format date to YYYY-MM-DD for date input
        let formattedDate = "";
        
        try {
            if (userData.dateOfBirth) {
                // Check if the date is valid
                const date = new Date(userData.dateOfBirth);
                if (!isNaN(date.getTime())) {
                    formattedDate = date.toISOString().split('T')[0];
                }
            }
        } catch (error) {
            console.error("Error formatting date:", error);
        }

        // Set form values
        setValue("username", userData.username || "");
        setValue("email", userData.email || "");
        setValue("phoneno", userData.phoneno || "");
        setValue("gender", userData.gender || "male");
        setValue("dateOfBirth", formattedDate);
        setValue("userType", userData.userType || "fan");
        setValue("country", userData.country || "");
        setValue("cityState", userData.cityState || "");
        
        // Set the image preview
        setPreviewImage(userData.image || userData.avatarUrl || "");
    }
}, [userData, setValue]);


  // const onSubmit = async (data) => {
  //   setButtonLoad(true);
  //   try {
  //     const response = await api.user.update({ id, ...data });
  //     if (response.data.success) {
  //       navigate("/admin/user");
  //     } else {
  //       setUpdateError(response.data.message);
  //       setSnackbarOpen(true);
  //     }
  //   } catch (error) {
  //     setUpdateError(error.response?.data?.message || "Error updating user");
  //     setSnackbarOpen(true);
  //   } finally {
  //     setButtonLoad(false);
  //   }
  // };

  const onSubmit = async (data) => {
    setButtonLoad(true);
    try {
        const formData = new FormData();
        
        // Add all form fields
        Object.keys(data).forEach(key => {
            formData.append(key, data[key]);
        });

        // Add image if selected
        if (selectedImage) {
            formData.append('image', selectedImage);
        }

        const response = await api.user.update({ id, formData });
        if (response.data.success) {
            navigate("/admin/user");
        } else {
            setUpdateError(response.data.message);
            setSnackbarOpen(true);
        }
    } catch (error) {
        setUpdateError(error.response?.data?.message || "Error updating user");
        setSnackbarOpen(true);
    } finally {
        setButtonLoad(false);
    }
};


  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Typography variant="h4">Edit User</Typography>
      </Stack>

      <Card sx={{ padding: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} textAlign="center">
              <Button
                component="label"
                variant="text"
                sx={{
                  padding: "3px",
                  margin: "10px",
                  borderRadius: "100px",
                  boxShadow: "0px 0px 10px",
                }}
              >
                <Avatar src={previewImage} sx={{ width: 100, height: 100 }} />
                <VisuallyHiddenInput
                  name="image"
                  accept="image/*"
                  type="file"
                  onChange={handleImageChange}
                />
              </Button>
              <Typography
                fontSize="0.75rem"
                marginTop="15px"
                marginBottom="20px"
                color="rgb(145, 158, 171)"
              >
                Allowed *.jpeg, *.jpg, *.png, *.gif <br />
                max size of 3 Mb
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                {...register("username")}
                error={!!errors.username}
                helperText={errors?.username?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                {...register("email")}
                error={!!errors.email}
                helperText={errors?.email?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                {...register("phoneno")}
                error={!!errors.phoneno}
                helperText={errors?.phoneno?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.gender}>
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  defaultValue={userData?.gender || "male"}
                  {...register("gender")}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register("dateOfBirth")}
                error={!!errors.dateOfBirth}
                helperText={errors?.dateOfBirth?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  label="User Type"
                  defaultValue={userData?.userType || "fan"}
                  {...register("userType")}
                >
                  <MenuItem value="fan">Fan</MenuItem>
                  <MenuItem value="artist">Artist</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                {...register("country")}
                error={!!errors.country}
                helperText={errors?.country?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City/State"
                {...register("cityState")}
                error={!!errors.cityState}
                helperText={errors?.cityState?.message}
              />
            </Grid>

            {/* <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                {...register("password")}
                error={!!errors.password}
                helperText={errors?.password?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                {...register("confirmPassword")}
                error={!!errors.confirmPassword}
                helperText={errors?.confirmPassword?.message}
              />
            </Grid> */}

            <Grid item xs={12}>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={buttonLoad}
                sx={{ mt: 2 }}
              >
                Update User
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {updateError}
        </Alert>
      </Snackbar>
    </Container>
  );
}
